import { useState, useRef, useEffect } from 'react';
import ScrollReveal from '../../../components/feature/ScrollReveal';

export default function Envelope() {
  const [isOpen, setIsOpen] = useState(false);
  const envelopeRef = useRef<HTMLDivElement>(null);
  const [envelopeH, setEnvelopeH] = useState(0);

  useEffect(() => {
    const calcOffset = () => {
      if (!envelopeRef.current) return;
      setEnvelopeH(envelopeRef.current.offsetHeight);
    };
    calcOffset();
    window.addEventListener('resize', calcOffset);
    return () => window.removeEventListener('resize', calcOffset);
  }, []);

  // Move letter up by 42% of envelope height
  const letterOffset = `-${Math.round(envelopeH * 0.42)}px`;

  return (
    <section className="relative py-16 sm:py-32 px-4 sm:px-6" style={{ background: '#F5F0E8' }}>
      <div className="relative z-10 max-w-4xl mx-auto">

        <ScrollReveal direction="up">
          <div className="text-center mb-10 sm:mb-16 space-y-3">
            <h2 className="text-4xl sm:text-6xl md:text-7xl font-serif italic text-[#2A1A14] mb-0 inline-block bg-[#F5F0E8] px-4 py-2">
              Приглашение
            </h2>
            <div className="flex items-center gap-3 justify-center">
              <div className="h-px w-12 bg-[#A8323C]/50" />
              <span className="text-[#A8323C] text-sm">✦</span>
              <div className="h-px w-12 bg-[#A8323C]/50" />
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal direction="up" delay={200}>
          <div
            className="relative mx-auto max-w-2xl md:max-w-3xl"
            style={{
              paddingTop: isOpen ? `${Math.round(envelopeH * 0.36)}px` : '0px',
              transition: 'padding-top 0.7s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            <div
              className="relative cursor-pointer select-none"
              onClick={() => setIsOpen(!isOpen)}
              style={{ perspective: '1200px' }}
            >
              {/* ── Envelope body ── */}
              <div
                ref={envelopeRef}
                className="relative rounded-sm"
                style={{
                  aspectRatio: '16/10',
                  overflow: 'visible',
                  background: '#6B1A2A',
                  boxShadow: '0 8px 40px rgba(74,16,24,0.35), inset 0 0 0 1px rgba(245,240,232,0.08)',
                }}
              >

                {/* ── Inner double border ── */}
                <div className="absolute inset-[10px] sm:inset-[14px] pointer-events-none z-10"
                  style={{ border: '1px solid rgba(245,240,232,0.30)' }} />
                <div className="absolute inset-[14px] sm:inset-[18px] pointer-events-none z-10"
                  style={{ border: '1px solid rgba(245,240,232,0.12)' }} />

                {/* ── Corner ornaments ── */}
                {(['top-[6px] left-[6px]', 'top-[6px] right-[6px]', 'bottom-[6px] left-[6px]', 'bottom-[6px] right-[6px]'] as const).map((pos, i) => (
                  <div key={i} className={`absolute ${pos} z-20 w-6 h-6 flex items-center justify-center`}>
                    <span className="font-serif leading-none text-[18px] sm:text-[22px]"
                      style={{ color: '#F5F0E8', opacity: 0.45 }}>✦</span>
                  </div>
                ))}

                {/* ── Fold lines ── */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-sm">
                  <svg viewBox="0 0 160 100" className="w-full h-full" preserveAspectRatio="none">
                    <line x1="0" y1="0" x2="80" y2="50" stroke="#3A0C14" strokeWidth="0.6" opacity="0.55" />
                    <line x1="160" y1="0" x2="80" y2="50" stroke="#3A0C14" strokeWidth="0.6" opacity="0.55" />
                    <line x1="0" y1="100" x2="80" y2="50" stroke="#3A0C14" strokeWidth="0.6" opacity="0.55" />
                    <line x1="160" y1="100" x2="80" y2="50" stroke="#3A0C14" strokeWidth="0.6" opacity="0.55" />
                  </svg>
                </div>

                {/* ── Hint text when closed ── */}
                {!isOpen && (
                  <div className="absolute inset-x-0 bottom-4 flex justify-center" style={{ zIndex: 5 }}>
                    <div className="flex items-center gap-2">
                      <div className="h-px w-5 bg-[#F5F0E8]/25" />
                      <p className="text-[#F5F0E8]/50 text-[9px] sm:text-xs tracking-[0.35em] uppercase font-mono">
                        Нажмите, чтобы открыть
                      </p>
                      <div className="h-px w-5 bg-[#F5F0E8]/25" />
                    </div>
                  </div>
                )}

                {/* ── Flap ── */}
                <div
                  className="absolute top-0 left-0 w-full transition-all duration-700 origin-top"
                  style={{
                    zIndex: 20,
                    transformStyle: 'preserve-3d',
                    transform: isOpen ? 'rotateX(-180deg)' : 'rotateX(0deg)',
                    height: '50%',
                  }}
                >
                  <svg viewBox="0 0 160 52" className="w-full h-full" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="flapGrad2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#7D1F30" />
                        <stop offset="100%" stopColor="#5A1522" />
                      </linearGradient>
                      {/* Subtle inner lining pattern on flap */}
                      <pattern id="flapDots" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                        <circle cx="5" cy="5" r="0.8" fill="#F5F0E8" opacity="0.18" />
                      </pattern>
                    </defs>
                    {/* Flap triangle */}
                    <polygon points="0,0 160,0 80,52" fill="url(#flapGrad2)" />
                    {/* Dot texture on flap */}
                    <polygon points="0,0 160,0 80,52" fill="url(#flapDots)" />
                    {/* Decorative edge line on flap */}
                    <polyline points="4,0 80,48 156,0" fill="none" stroke="#F5F0E8" strokeWidth="0.5" opacity="0.25" />
                    <polyline points="10,0 80,44 150,0" fill="none" stroke="#F5F0E8" strokeWidth="0.3" opacity="0.12" />
                    {/* Small ornament at flap tip */}
                    <text x="80" y="34" textAnchor="middle" fontSize="6" fill="#F5F0E8" opacity="0.35" fontFamily="serif">✦</text>
                  </svg>

                  {/* Wax seal */}
                  <div
                    className="absolute left-1/2 -translate-x-1/2 transition-all duration-500"
                    style={{
                      bottom: '-28px',
                      zIndex: 30,
                      opacity: isOpen ? 0 : 1,
                      transform: `translateX(-50%) ${isOpen ? 'scale(0.7)' : 'scale(1)'}`,
                    }}
                  >
                    <div className="relative">
                      {/* Outer glow ring */}
                      <div className="absolute inset-0 rounded-full"
                        style={{ boxShadow: '0 0 0 4px rgba(168,50,60,0.20), 0 0 0 7px rgba(168,50,60,0.08)' }} />
                      <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-[#4A1018] border-2 border-[#F5F0E8]/35 flex items-center justify-center"
                        style={{ boxShadow: '0 3px 14px rgba(74,16,24,0.5)' }}>
                        <img
                          src="/images/pango.jpeg"
                          alt="Seal"
                          className="w-7 h-7 sm:w-9 sm:h-9 object-contain rounded-full opacity-90"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Letter card ── */}
                <div
                  className="absolute inset-x-3 sm:inset-x-6 md:inset-x-10 transition-all duration-700 ease-out"
                  style={{
                    zIndex: isOpen ? 30 : 1,
                    bottom: '8%',
                    transform: isOpen ? `translateY(${letterOffset})` : 'translateY(0%)',
                    opacity: isOpen ? 1 : 0,
                    pointerEvents: isOpen ? 'auto' : 'none',
                  }}
                >
                  <div className="relative bg-[#FDFAF4] rounded-sm px-4 sm:px-8 md:px-14 py-4 sm:py-7 md:py-10 text-center"
                    style={{
                      boxShadow: '0 6px 30px rgba(42,26,20,0.18)',
                      border: '1px solid rgba(168,50,60,0.18)',
                      outline: '1px solid rgba(168,50,60,0.08)',
                      outlineOffset: '5px',
                    }}>

                    {/* Inner corner ornaments on letter */}
                    {(['top-2 left-2', 'top-2 right-2', 'bottom-2 left-2', 'bottom-2 right-2'] as const).map((pos, i) => (
                      <span key={i}
                        className={`absolute ${pos} font-serif text-[#A8323C]/30 text-[11px] leading-none`}>✦</span>
                    ))}

                    {/* Inner decorative border */}
                    <div className="absolute inset-[10px] border border-[#A8323C]/10 pointer-events-none rounded-sm" />

                    <div className="space-y-3 sm:space-y-4 md:space-y-5">
                      {/* Top ornament */}
                      <div className="flex items-center gap-2 justify-center">
                        <div className="h-px w-8 bg-[#A8323C]/30" />
                        <span className="text-[#A8323C]/50 text-[10px]">✦</span>
                        <div className="h-px w-3 bg-[#A8323C]/20" />
                        <span className="text-[#A8323C]/30 text-[8px]">✦</span>
                        <div className="h-px w-3 bg-[#A8323C]/20" />
                        <span className="text-[#A8323C]/50 text-[10px]">✦</span>
                        <div className="h-px w-8 bg-[#A8323C]/30" />
                      </div>

                      <p className="text-sm sm:text-lg md:text-xl font-serif italic text-[#2A1A14] leading-relaxed">
                        Дорогие друзья и близкие!
                      </p>

                      <p className="text-xs sm:text-sm md:text-base text-[#6B5040] leading-relaxed">
                        Мы рады пригласить вас разделить с нами<br />
                        один из самых важных дней в нашей жизни.<br />
                        Ваше присутствие сделает этот день<br />
                        по-настоящему особенным.
                      </p>

                      {/* Divider */}
                      <div className="flex items-center gap-3 justify-center py-1">
                        <div className="h-px flex-1 bg-[#A8323C]/20" />
                        <span className="text-[#A8323C]/40 text-xs font-serif">✦</span>
                        <div className="h-px flex-1 bg-[#A8323C]/20" />
                      </div>

                      <div className="space-y-1 sm:space-y-2">
                        <p className="text-sm sm:text-base md:text-lg font-serif italic text-[#6B1A2A] font-semibold">
                          25 июля 2026 года, суббота
                        </p>
                        <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#8B6B5A] font-mono">
                          С любовью, Сергей &amp; Екатерина
                        </p>
                      </div>

                      {/* Bottom ornament */}
                      <div className="flex items-center gap-2 justify-center pt-1">
                        <div className="h-px w-8 bg-[#A8323C]/30" />
                        <span className="text-[#A8323C]/50 text-[10px]">✦</span>
                        <div className="h-px w-3 bg-[#A8323C]/20" />
                        <span className="text-[#A8323C]/30 text-[8px]">✦</span>
                        <div className="h-px w-3 bg-[#A8323C]/20" />
                        <span className="text-[#A8323C]/50 text-[10px]">✦</span>
                        <div className="h-px w-8 bg-[#A8323C]/30" />
                      </div>
                    </div>

                  </div>
                </div>

              </div>
            </div>
          </div>
        </ScrollReveal>

      </div>
    </section>
  );
}
